import React, { useState } from 'react'
import DriverAvatar from './DriverAvatar'

interface Driver { number: number; name: string; team: string }
interface Assignment { playerName: string; drivers: Driver[] }

interface Props {
  players: string[]
  drivers: Driver[]
  onComplete: (assignments: Assignment[]) => void
  onBack: () => void
}

export default function ManualAssignment({ players, drivers, onComplete, onBack }: Props) {
  const [rosters, setRosters]   = useState<Record<string, Driver[]>>(
    Object.fromEntries(players.map(p => [p, []]))
  )
  const [selected, setSelected] = useState<Driver | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)

  // Derive the unassigned pool from rosters
  const assignedNums = new Set(Object.values(rosters).flatMap(ds => ds.map(d => d.number)))
  const unassigned   = drivers.filter(d => !assignedNums.has(d.number))
  const totalAssigned = drivers.length - unassigned.length
  const allAssigned   = unassigned.length === 0

  // ── Click-to-assign ──────────────────────────────────────────────────────
  const handleDriverClick = (driver: Driver) => {
    setSelected(prev => prev?.number === driver.number ? null : driver)
  }

  const handlePlayerClick = (playerName: string) => {
    if (!selected) return
    setRosters(prev => ({ ...prev, [playerName]: [...prev[playerName], selected] }))
    setSelected(null)
  }

  const handleUnassign = (playerName: string, driver: Driver, e: React.MouseEvent) => {
    e.stopPropagation()
    setRosters(prev => ({ ...prev, [playerName]: prev[playerName].filter(d => d.number !== driver.number) }))
  }

  // ── Drag-and-drop (desktop) ──────────────────────────────────────────────
  const [dragging, setDragging] = useState<{ driver: Driver } | null>(null)

  const handleDragStart = (driver: Driver) => {
    setDragging({ driver })
    setSelected(null)
  }

  const handleDrop = (playerName: string) => {
    if (!dragging) return
    setRosters(prev => ({ ...prev, [playerName]: [...prev[playerName], dragging.driver] }))
    setDragging(null)
    setDragOver(null)
  }

  const handleDragFromRoster = (driver: Driver, fromPlayer: string) => {
    // Remove from current player, start drag
    setRosters(prev => ({ ...prev, [fromPlayer]: prev[fromPlayer].filter(d => d.number !== driver.number) }))
    setDragging({ driver })
  }

  const handleDropToPool = () => {
    // Dropping back onto pool = unassign
    setDragging(null)
    setDragOver(null)
  }

  const handleComplete = () => {
    onComplete(players.map(p => ({ playerName: p, drivers: rosters[p] })))
  }

  const progress = Math.round((totalAssigned / drivers.length) * 100)

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
        <div className="section-title" style={{ margin: 0 }}>✋ Manual Assignment</div>
        <div style={{ fontSize: '0.82rem', color: allAssigned ? '#4caf50' : 'rgba(255,255,255,0.5)' }}>
          {totalAssigned} / {drivers.length} assigned
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginBottom: '0.8rem' }}>
        <div style={{
          height: '100%', borderRadius: 2, transition: 'width 0.3s',
          background: allAssigned ? '#4caf50' : '#e8b84b',
          width: `${progress}%`
        }} />
      </div>

      {/* Instruction banner */}
      <div style={{
        borderRadius: 8, padding: '0.55rem 1rem', marginBottom: '1rem',
        fontSize: '0.82rem',
        background: selected
          ? 'rgba(232,184,75,0.15)' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${selected ? 'rgba(232,184,75,0.45)' : 'rgba(255,255,255,0.1)'}`,
        color: selected ? '#e8b84b' : 'rgba(255,255,255,0.45)',
      }}>
        {selected
          ? `#${selected.number} ${selected.name} selected — tap a player card to assign`
          : 'Tap a driver to select · tap a player card to assign · tap ✕ to unassign'}
      </div>

      {/* ── Two-panel layout ── */}
      <div className="manual-assign-grid">

        {/* LEFT: Unassigned pool */}
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.35)', marginBottom: '0.5rem' }}>
            Unassigned ({unassigned.length})
          </div>
          <div
            className="driver-pool-list"
            onDragOver={e => e.preventDefault()}
            onDrop={handleDropToPool}
          >
            {unassigned.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#4caf50', fontSize: '0.85rem' }}>
                ✅ All drivers assigned!
              </div>
            ) : unassigned.map(d => {
              const isSelected = selected?.number === d.number
              return (
                <div
                  key={d.number}
                  draggable
                  onDragStart={() => handleDragStart(d)}
                  onDragEnd={() => setDragging(null)}
                  onClick={() => handleDriverClick(d)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.45rem 0.6rem', borderRadius: 8, cursor: 'pointer',
                    background: isSelected ? 'rgba(232,184,75,0.18)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isSelected ? 'rgba(232,184,75,0.55)' : 'rgba(255,255,255,0.07)'}`,
                    transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                    transition: 'all 0.12s',
                    userSelect: 'none',
                  }}
                >
                  <DriverAvatar name={d.name} number={d.number} size={26} />
                  <div style={{ fontSize: '0.72rem', color: '#e8b84b', fontWeight: 700, minWidth: 26 }}>#{d.number}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', color: '#f0f0f0', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.38)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.team}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT: Player rosters */}
        <div className="player-roster-list">
          {players.map(playerName => {
            const pDrivers  = rosters[playerName]
            const isDropTarget = !!selected || dragOver === playerName
            return (
              <div
                key={playerName}
                draggable={false}
                onDragOver={e => { e.preventDefault(); setDragOver(playerName) }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop(playerName)}
                onClick={() => handlePlayerClick(playerName)}
                style={{
                  background: isDropTarget ? 'rgba(232,184,75,0.08)' : 'var(--card)',
                  border: `${isDropTarget ? 2 : 1}px solid ${isDropTarget ? 'rgba(232,184,75,0.5)' : 'var(--border)'}`,
                  borderRadius: 10, overflow: 'hidden',
                  cursor: selected ? 'pointer' : 'default',
                  transition: 'all 0.12s',
                }}
              >
                {/* Card header */}
                <div style={{
                  padding: '0.45rem 0.7rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                  background: isDropTarget ? 'rgba(232,184,75,0.06)' : 'transparent',
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.83rem', color: '#fff' }}>{playerName}</span>
                  <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '0.1rem 0.45rem' }}>
                    {pDrivers.length} drivers
                  </span>
                </div>

                {/* Assigned drivers */}
                <div style={{ padding: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', minHeight: 32 }}>
                  {selected && pDrivers.length === 0 && (
                    <div style={{ color: 'rgba(232,184,75,0.55)', fontSize: '0.72rem', padding: '0.3rem', textAlign: 'center' }}>
                      Tap to assign #{selected.number} {selected.name}
                    </div>
                  )}
                  {pDrivers.map(d => (
                    <div
                      key={d.number}
                      draggable
                      onDragStart={e => { e.stopPropagation(); handleDragFromRoster(d, playerName) }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        padding: '0.28rem 0.4rem', borderRadius: 6,
                        background: 'rgba(255,255,255,0.04)',
                        fontSize: '0.75rem', userSelect: 'none',
                      }}
                      onClick={e => handleUnassign(playerName, d, e)}
                    >
                      <span style={{ color: '#e8b84b', fontWeight: 700, minWidth: 22, fontSize: '0.7rem' }}>#{d.number}</span>
                      <span style={{ color: '#e0e0e0', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem', flexShrink: 0 }}>✕</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', gap: '1rem' }}>
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={!allAssigned}
          style={{ opacity: allAssigned ? 1 : 0.45 }}
        >
          {allAssigned ? '✅ Save Rosters & Continue' : `${unassigned.length} drivers left to assign`}
        </button>
      </div>
    </div>
  )
}
